import { useState, useEffect, useCallback } from "react";
import { Formik, Form } from "formik";
import { Link } from "react-router-dom";
import * as Yup from "yup";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Button,
  Typography,
  Container,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  Chip,
} from "@mui/material";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { createReservationRequest } from "./ScheduleApi";

import { useAuth0 } from "@auth0/auth0-react";

import ReservationSuccessModal from "./ReservationSuccessModal";
import { ReservationFailureModal } from "./ReservationFailureModal";
import { useClient } from './ClientContext';
import { useActiveTokenCheck } from './hooks/useActiveTokenCheck';

import PatientCardSelector from './components/PatientCardSelector';
import PatientCardRegistration from './components/PatientCardRegistration';
import { PatientCard } from './API';
import { getPatientCards } from './graphql/queries';
import { createPatientCard, deletePatientCard } from './graphql/mutations';

// バリデーションスキーマ
const validationSchema = Yup.object({
  selectedCardId: Yup.string(),
  patientCardNumber: Yup.string().when('selectedCardId', {
    is: 'new',
    then: (schema) => schema.required("診察券番号は必須です"),
    otherwise: (schema) => schema,
  }),
  birth: Yup.string().when('selectedCardId', {
    is: 'new',
    then: (schema) => schema
      .required("生年月日は必須です。")
      .matches(/^(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])$/, "形式が無効です。"),
    otherwise: (schema) => schema,
  }),
  reservationDate: Yup.string().required("予約日は必須です"),
  partOfDay: Yup.string().required("時間帯は必須です"),
  nickname: Yup.string(),
  saveCard: Yup.boolean(),
});

const SekineJibikaExist = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [modalOpen, setModalOpen] = useState(false);
  const [failureModalOpen, setFailureModalOpen] = useState(false);
  const [paypayUrl, setPaypayUrl] = useState<string | undefined>(undefined);
  const [patientCards, setPatientCards] = useState<PatientCard[]>([]);
  const { checkAndPromptIfNeeded } = useActiveTokenCheck();
  const [isLoadingCards, setIsLoadingCards] = useState(false); // 初期値をfalseに変更
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [cardFeatureAvailable, setCardFeatureAvailable] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false); // 初期化フラグを追加
  
  const today = dayjs();
  const oneMonthLater = today.add(1, "month");
  const { client } = useClient();

  // 診察券一覧を取得
  const fetchPatientCards = useCallback(async () => {
    if (!client) {
      return;
    }
    
    // 初回の読み込み時のみローディングを表示
    if (!hasInitialized) {
      setIsLoadingCards(true);
    }
    
    try {
      const result = await client.graphql({
        query: getPatientCards,
        variables: { hospitalName: 'sekine-jibika' }
      });
      
      const cards = result.data.getPatientCards?.filter((card: PatientCard | null): card is PatientCard => card !== null) || [];
      // 最新の診察券が上に来るようにソート（createdAtで降順）
      const sortedCards = [...cards].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0).getTime();
        const dateB = new Date(b.createdAt || 0).getTime();
        return dateB - dateA; // 降順（新しい順）
      });
      setPatientCards(sortedCards);
      setCardFeatureAvailable(true);
    } catch (error: any) {
      console.error('診察券の取得に失敗しました:', error);
      
      // 502エラーの場合は機能が利用できないことを示す
      if (error?.errors?.[0]?.message?.includes('502') || 
          error?.errors?.[0]?.errorType === 'BadGateway' ||
          error?.response?.status === 502) {
        console.log('診察券機能は現在利用できません。通常の入力フォームを表示します。');
        setCardFeatureAvailable(false);
        // エラーメッセージは表示しない（機能が未実装の可能性があるため）
      } else {
        // その他のエラーの場合はメッセージを表示
        setSnackbarMessage('診察券の取得に失敗しました');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } finally {
      setIsLoadingCards(false);
      setHasInitialized(true);
    }
  }, [client, hasInitialized]);

  useEffect(() => {
    if (client) {
      fetchPatientCards();
    }
  }, [client, fetchPatientCards]);

  // 診察券削除の確認ダイアログを開く
  const handleDeleteClick = (cardId: string) => {
    setCardToDelete(cardId);
    setDeleteDialogOpen(true);
  };

  // 診察券削除の実行
  const handleConfirmDelete = async () => {
    if (!cardToDelete || !client) return;

    try {
      const result = await client.graphql({
        query: deletePatientCard,
        variables: { cardId: cardToDelete }
      });

      if (result.data.deletePatientCard.success) {
        await fetchPatientCards();
        setSnackbarMessage('診察券を削除しました');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      } else {
        throw new Error('削除に失敗しました');
      }
    } catch (error) {
      console.error('診察券の削除に失敗しました:', error);
      setSnackbarMessage('診察券の削除に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
      setCardToDelete(null);
    }
  };

  // 診察券の新規登録
  const registerPatientCard = async (
    patientCardNumber: string,
    patientBirth: string,
    nickname?: string
  ) => {
    if (!client) return;

    try {
      const result = await client.graphql({
        query: createPatientCard,
        variables: {
          hospitalName: 'sekine-jibika',
          patientCardNumber,
          patientBirth,
          nickname: nickname || undefined,
        }
      });

      if (result.data.createPatientCard.success) {
        await fetchPatientCards();
        setSnackbarMessage('診察券を保存しました');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('診察券の保存に失敗しました:', error);
      setSnackbarMessage('診察券の保存に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth={false} sx={{ maxWidth: 760, p: { xs: 2.5, sm: 5 }, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: "24px" }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
          <Typography variant="body2" color="primary" sx={{ fontWeight: 700 }}>
            せきね耳鼻咽喉科
          </Typography>
          <Chip label="診察券あり" size="small" sx={{ bgcolor: '#E1F1EF', color: 'primary.main', fontWeight: 600 }} />
        </Box>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 700, letterSpacing: '-0.04em', mb: 3 }}>
          診察の予約
        </Typography>
        
        <Formik
          initialValues={{
            selectedCardId: "",
            patientCardNumber: "",
            birth: "",
            reservationDate: null as Dayjs | null,
            partOfDay: "0",
            nickname: "",
            saveCard: false,
          }}
          validationSchema={validationSchema}
          onSubmit={async (values, { setSubmitting }) => {
            // Check token validity first
            const isTokenValid = await checkAndPromptIfNeeded();
            if (!isTokenValid) {
              setSubmitting(false);
              return;
            }

            const formattedDate = values.reservationDate
              ? values.reservationDate.format("YYYY-MM-DD")
              : "";

            try {
              await getAccessTokenSilently();
              let appointmentData: any;

              if (values.selectedCardId && values.selectedCardId !== 'new') {
                // 選択された診察券を使用
                const selectedCard = patientCards.find(card => card.cardId === values.selectedCardId);
                if (!selectedCard) {
                  setFailureModalOpen(true);
                  setSubmitting(false);
                  return;
                }
                
                appointmentData = {
                  cardId: values.selectedCardId,
                  patientCardNumber: selectedCard.patientCardNumber,
                  patientBirth: selectedCard.patientBirth,
                  nickname: selectedCard.nickname,
                  reservationDate: formattedDate,
                  hospitalName: "sekine-jibika",
                  partOfDay: values.partOfDay,
                };
              } else {
                // 新規入力の場合
                appointmentData = {
                  patientCardNumber: values.patientCardNumber,
                  patientBirth: values.birth,
                  nickname: values.nickname || undefined,
                  reservationDate: formattedDate,
                  hospitalName: "sekine-jibika",
                  partOfDay: values.partOfDay,
                };

                // 保存オプションがONの場合（診察券機能が利用可能な場合のみ）
                if (values.saveCard && cardFeatureAvailable) {
                  await registerPatientCard(
                    values.patientCardNumber,
                    values.birth,
                    values.nickname
                  );
                }
              }

              const result = await createReservationRequest(client, appointmentData);
              if (result.success) {
                setPaypayUrl(result.paypayUrl || undefined);
                setModalOpen(true);
              } else {
                setFailureModalOpen(true);
              }
            } catch (error) {
              console.error("Reservation request failed:", error);
              setFailureModalOpen(true);
            }
            setSubmitting(false);
          }}
        >
          {({
            errors,
            touched,
            handleChange,
            handleBlur,
            isSubmitting,
            setFieldValue,
            values,
          }) => (
            <Form>
              {/* 診察券選択セクション（機能が利用可能な場合のみ） */}
              {cardFeatureAvailable && (patientCards.length > 0 || isLoadingCards) && (
                <PatientCardSelector
                  patientCards={patientCards}
                  selectedCardId={values.selectedCardId}
                  onCardSelect={(cardId) => {
                    setFieldValue('selectedCardId', cardId);
                    if (cardId !== 'new') {
                      // 選択された診察券の情報を自動入力
                      const selectedCard = patientCards.find(card => card.cardId === cardId);
                      if (selectedCard) {
                        setFieldValue('patientCardNumber', selectedCard.patientCardNumber);
                        setFieldValue('birth', selectedCard.patientBirth);
                      }
                    } else {
                      // 新規登録を選択した場合はクリア
                      setFieldValue('patientCardNumber', '');
                      setFieldValue('birth', '');
                    }
                  }}
                  onCardDelete={handleDeleteClick}
                  isLoading={isLoadingCards}
                />
              )}

              {/* 新規入力セクション（診察券機能が利用できない場合、診察券がない場合、または「新規登録」選択時） */}
              {(!cardFeatureAvailable || (patientCards.length === 0 && !isLoadingCards) || values.selectedCardId === 'new') && (
                <PatientCardRegistration
                  patientCardNumber={values.patientCardNumber}
                  birth={values.birth}
                  nickname={values.nickname}
                  saveCard={cardFeatureAvailable ? values.saveCard : false}
                  onPatientCardNumberChange={(value) => setFieldValue('patientCardNumber', value)}
                  onBirthChange={(value) => setFieldValue('birth', value)}
                  onNicknameChange={(value) => setFieldValue('nickname', value)}
                  onSaveCardChange={cardFeatureAvailable ? (checked) => setFieldValue('saveCard', checked) : undefined}
                  errors={errors}
                  touched={touched}
                />
              )}

              {/* 予約日・時間帯選択 */}
              <Box sx={{ mt: 3.5, pt: 3.5, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography component="h2" variant="h6" sx={{ mb: 1.5 }}>予約日と時間帯</Typography>
                <FormControl fullWidth margin="normal">
                  <DatePicker
                    label="予約日"
                    slotProps={{ textField: { helperText: "祝日・臨時休業日を除く" } }}
                    minDate={today}
                    maxDate={oneMonthLater}
                    value={values.reservationDate}
                    onChange={(newValue) => {
                      setFieldValue("reservationDate", newValue);
                    }}
                    format="YYYY-MM-DD"
                    closeOnSelect
                    shouldDisableDate={(date) => {
                      // 日曜日 (0) と水曜日 (3) を禁止
                      return date.day() === 0 || date.day() === 3;
                    }}
                  />
                </FormControl>

                <FormControl component="fieldset" fullWidth margin="normal">
                  <FormLabel component="legend" sx={{ mb: 1.5, fontSize: ".875rem", fontWeight: 600 }}>時間帯</FormLabel>
                  <RadioGroup
                    name="partOfDay"
                    value={values.partOfDay}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    row
                    sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}
                  >
                    <FormControlLabel
                      value="0"
                      control={<Radio />}
                      label="午前(9:30～)"
                      sx={{ m: 0, px: 1.5, py: 1, border: '1px solid', borderRadius: "12px", borderColor: values.partOfDay === '0' ? 'primary.main' : 'divider', bgcolor: values.partOfDay === '0' ? '#E1F1EF' : 'transparent', '& .MuiFormControlLabel-label': { fontWeight: 600 } }}
                    />
                    <FormControlLabel
                      value="1"
                      control={<Radio />}
                      label="午後(15:00～)"
                      sx={{ m: 0, px: 1.5, py: 1, border: '1px solid', borderRadius: "12px", borderColor: values.partOfDay === '1' ? 'primary.main' : 'divider', bgcolor: values.partOfDay === '1' ? '#E1F1EF' : 'transparent', '& .MuiFormControlLabel-label': { fontWeight: 600 } }}
                    />
                  </RadioGroup>
                </FormControl>

              </Box>
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px dashed', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2, lineHeight: 1.8 }}>依頼時点では、医院の予約は未確定です。</Typography>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || (!values.selectedCardId && patientCards.length > 0)}
                  fullWidth
                  sx={{ fontSize: "1rem", py: 1.75 }}
                >
                  自動予約を依頼する
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
        <Box component="nav" aria-label="予約方法の切り替え" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, justifyContent: 'center' }}>
          <Button component={Link} to="/reservation/sekine-jibika/new" size="small" sx={{ px: 1, minHeight: 36, fontSize: '12px', color: 'text.secondary', fontWeight: 400 }}>
            診察券なしで予約
          </Button>
          <Button component={Link} to="/reservation/new" size="small" sx={{ px: 1, minHeight: 36, fontSize: '12px', color: 'text.secondary', fontWeight: 400 }}>
            他の医院を選ぶ
          </Button>
        </Box>

        {/* 削除確認ダイアログ */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>診察券を削除しますか？</DialogTitle>
          <DialogContent>
            <DialogContentText>
              この診察券を削除します。この操作は取り消せません。
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>
              キャンセル
            </Button>
            <Button onClick={handleConfirmDelete} color="error">
              削除
            </Button>
          </DialogActions>
        </Dialog>

        {/* スナックバー */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={() => setSnackbarOpen(false)}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{ width: '100%' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <ReservationSuccessModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          paypayUrl={paypayUrl}
        />
        <ReservationFailureModal
          open={failureModalOpen}
          onClose={() => setFailureModalOpen(false)}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default SekineJibikaExist;